<template lang="pug">
.model-option
    .model-option__head
        span.model-option__name {{ model.label }}
        span.model-option__price(v-if="model.priceText || model.effectivePrice") {{ priceLabel }}
    .model-option__description(v-if="summary" :title="model.description") {{ summary }}
</template>

<i18n>
{
    "en": {
        "unit.seconds": "second | seconds",
        "unit.images": "image | images"
    },
    "de": {
        "up to": "bis zu",
        "unit.seconds": "Sekunde | Sekunden",
        "unit.images": "Bild | Bilder"
    }
}
</i18n>

<script>
import { preciseMoneyFormat } from '@/filters';

/**
 * One row in a generative-media model picker.
 *
 * The four model dropdowns (image editor, video drawer, prompt template, shop
 * settings) each built their own "Name · up to X €/unit" string, and had drifted
 * apart: the settings screen rounded to 0,72 € where the editor showed 0,715 €
 * for the SAME model. One component now owns the layout and the formatting, so
 * they cannot disagree again.
 *
 * Layout is a price list, not a sentence: the price is right-aligned in tabular
 * figures so the decimals line up down the list and models can be compared at a
 * glance — which is the only reason the price is in the picker at all.
 */
export default {
    name: 'ModelSelectOption',
    props: {
        model: {
            type: Object,
            required: true,
        },
    },
    computed: {
        /**
         * Just the opening clause of the model's description — "Cheapest general edit",
         * not the full sentence with its caveats. What distinguishes one model from
         * another is always stated first; the rest is detail that belongs in the
         * tooltip, and at full length it turned the picker into a wall of prose.
         *
         * Splits on the first `;` or sentence-ending `.` (a period must be followed by
         * whitespace or end-of-string, so a decimal in the text never splits it).
         */
        summary() {
            const description = (this.model.description || '').trim();
            if (!description) return null;

            const [, opening] = description.match(/^(.*?)[;.](?:\s|$)/) || [];
            const summary = (opening || description).trim();

            // Several descriptions open by restating the model name ("Kling v3 Std" →
            // "Kling v3 Standard."), which would print the label twice. Compare loosely,
            // since the two spellings differ in punctuation and abbreviation.
            const normalize = text => text.toLowerCase().replace(/[^a-z0-9]/g, '');
            const label = normalize(this.model.label || '');
            const candidate = normalize(summary);
            if (label && candidate && (candidate.startsWith(label) || label.startsWith(candidate))) {
                return null;
            }

            return summary;
        },
        priceLabel() {
            // A model priced in more than one figure (a text model: input and output tokens) brings its own text.
            if (this.model.priceText) {
                return this.model.priceText;
            }

            const price = preciseMoneyFormat(this.model.effectivePrice);
            const unit = this.$tc('unit.' + this.model.unit, 1);

            return `${this.$t('up to')} ${price}/${unit}`;
        },
    },
};
</script>

<style lang="sass" scoped>
.model-option
    padding: 2px 0
    // The row owns its own line-height: Element's option sets 34px to centre a
    // single line, which would clip the second one.
    line-height: 1.35

    &__head
        display: flex
        align-items: baseline
        justify-content: space-between
        gap: 16px

    &__name
        font-size: 13px
        color: inherit

    &__price
        flex: none
        font-size: 12px
        color: var(--color-text-muted, #909399)
        // Aligns the decimal columns down the list so prices compare vertically.
        font-variant-numeric: tabular-nums

    &__description
        margin-top: 1px
        font-size: 12px
        color: #c0c4cc
        // Descriptions are a sentence long; one line keeps the list scannable
        // and the full text stays available on hover.
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap
</style>
